/**
 * Sessions list page for the plugin builder.
 * Shows all plugin builder sessions with create/delete actions.
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePluginBuilderSessions } from '../hooks/usePluginBuilderSessions';
import { FavoriteButton } from '../../../components/FavoriteButton';
import { NewArcheDialog } from '../components/NewArcheDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Loader2,
  Plus,
  Trash2,
  Clock,
  Code2,
  LayoutDashboard,
  Info,
  Globe,
  Hammer,
  Lock,
  Store,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { formatCredits, formatDollars } from '@/lib/format-credits';
import type { PluginBuilderSession } from '../types';
import { useCreateForgeArche, useForgeAvailability } from '../../forge/hooks';
import { useAuth } from '@/auth/hooks/useAuth';
import { ApiError } from '@/lib/api';
import { FORGE_ARCHE_ID } from '../components/ForgeMigratedBanner';

export function PluginSessionsListPage() {
  const navigate = useNavigate();
  const { sessions, isLoading, isCreating, error, createSession, deleteSession } =
    usePluginBuilderSessions();
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [newDialogOpen, setNewDialogOpen] = useState(false);

  const { isGuest } = useAuth();
  const openNewArcheDialog = () => setNewDialogOpen(true);
  // Fias Forge is a first-class way to start a new arche (ADR-023), offered
  // only when the kill switch is on; the plan check drives the card's state.
  // Never for a guest-trial account: Forge is full-account-only, and even
  // asking (`/forge/usage`) would bounce the guest to sign-up.
  const forgeAvailability = useForgeAvailability({ enabled: !isGuest });
  // A guest at GUEST_MAX_BUILDER_SESSIONS gets a sign-up prompt, not a dead end.
  const guestSessionLimitReached =
    error instanceof ApiError && error.code === 'GUEST_BUILDER_SESSION_LIMIT';
  // The server states the limit (GUEST_MAX_BUILDER_SESSIONS lives server-side).
  const guestSessionLimit = guestSessionLimitReached
    ? (error.body as { error?: { limit?: unknown } } | undefined)?.error?.limit
    : undefined;
  const createForgeArche = useCreateForgeArche();

  const handleModeChosen = async (mode: 'chat' | 'guided') => {
    setNewDialogOpen(false);
    await createSession({ mode });
  };

  const handleDeleteSession = async (sessionId: string) => {
    await deleteSession(sessionId);
  };

  const statusColors: Record<string, string> = {
    active: 'bg-green-500/10 text-green-600 border-green-500/20',
    paused: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    completed: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    abandoned: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  };

  function getPublishBadge(session: PluginBuilderSession) {
    if (!session.archId) return null;
    const s = session.archeStatus;
    if (s === 'published')
      return {
        label: 'Published',
        className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
      };
    if (s === 'suspended')
      return { label: 'Suspended', className: 'bg-red-500/10 text-red-600 border-red-500/20' };
    if (s === 'deprecated')
      return {
        label: 'Deprecated',
        className: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
      };
    if (s === 'testing')
      return {
        label: 'Testing',
        className: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
      };
    return { label: 'Draft', className: 'bg-gray-500/10 text-gray-500 border-gray-500/20' };
  }

  function formatPricing(session: PluginBuilderSession): string {
    const p = session.archePricing;
    if (!p) return 'Free';
    if (p.model === 'free') return 'Free';
    if (p.model === 'fixed' && p.priceCents != null)
      return `${(p.priceCents / 100).toFixed(2)}/use`;
    if (p.model === 'per_use' && p.baseFeeCents != null)
      return `${(p.baseFeeCents / 100).toFixed(2)}+/use`;
    if (p.model === 'tiered') return 'Tiered';
    return p.model;
  }

  return (
    <>
      <NewArcheDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        onSelectMode={handleModeChosen}
        isCreating={isCreating}
        forge={{ offered: forgeAvailability.offered, eligible: forgeAvailability.eligible }}
        onCreateForge={createForgeArche}
      />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold">Arche Builder</h1>
            <Link
              to="/marketplace/arches/arche_plugin_builder"
              className="text-muted-foreground hover:text-primary transition-colors"
              title="View arche details"
            >
              <Info className="h-6 w-6" />
            </Link>
            <FavoriteButton archeId="arche_plugin_builder" />
          </div>
          <p className="text-muted-foreground mt-1">
            Create custom Arches through conversations with Fias AI
          </p>
          <p className="text-muted-foreground text-sm mt-1">
            (The Arche Builder is Beta right now, and still has bugs. The external plugin system is
            the most robust way to build arches right now)
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button onClick={openNewArcheDialog} disabled={isCreating}>
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              New Arche
            </Button>
            {!isGuest && (
              <Button variant="outline" onClick={() => navigate('/contributor/dashboard')}>
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Arche Dashboard
              </Button>
            )}
            {/* The user's forges, whatever their plan: a downgraded user must
                still reach a forge to open or delete it. Hidden while Forge
                is switched off, and never for a guest (availability is not
                even fetched for one). */}
            {forgeAvailability.offered && (
              <Button variant="outline" asChild>
                <Link to={`/a/${FORGE_ARCHE_ID}`}>
                  <Hammer className="h-4 w-4 mr-2" />
                  Fias Forge
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* Guest trial: the in-progress app limit is a sign-up moment. */}
        {guestSessionLimitReached && (
          <Card className="mb-6 border-primary/40" role="alert">
            <CardContent className="pt-6 space-y-3">
              <p className="font-medium">
                {typeof guestSessionLimit === 'number'
                  ? `Your free trial can have ${guestSessionLimit} apps in progress`
                  : 'Your free trial has reached its limit of apps in progress'}
              </p>
              <p className="text-sm text-muted-foreground">
                Create your free account to start another one, or delete an app you don&apos;t need.
                Everything you&apos;ve built comes with you when you sign up.
              </p>
              <Button onClick={() => navigate('/auth/signup?reason=create-account')}>
                Create free account
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {error && !guestSessionLimitReached && (
          <Card className="mb-6 border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">{error.message}</p>
            </CardContent>
          </Card>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && sessions.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                <Code2 className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No arches yet</h3>
              <p className="text-muted-foreground mb-4">
                Start a new session to begin building your arche with React and TypeScript
              </p>
              <div className="flex justify-center gap-2">
                <Button onClick={openNewArcheDialog} disabled={isCreating}>
                  {isCreating ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  New Arche
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sessions grid */}
        {!isLoading && sessions.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sessions.map((session) => (
              <Card
                key={session.sessionId}
                className="group hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/a/arche_plugin_builder/${session.sessionId}`)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">
                        {session.currentName || 'Untitled Arche'}
                      </CardTitle>
                      <CardDescription className="mt-1 line-clamp-2">
                        {session.currentDescription || 'No description'}
                      </CardDescription>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <Badge variant="outline" className={statusColors[session.status]}>
                        {session.status}
                      </Badge>
                      {(() => {
                        const pub = getPublishBadge(session);
                        return pub ? (
                          <Badge variant="outline" className={pub.className}>
                            {pub.label}
                          </Badge>
                        ) : null;
                      })()}
                      {session.migratedToForgeId && (
                        <Badge
                          variant="outline"
                          className="bg-amber-500/10 text-amber-600 border-amber-500/20"
                          title="Moved to Fias Forge — this session is read-only"
                        >
                          In Forge
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {session.archId && (
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                      <span className="flex items-center gap-1">
                        {session.archeIsPublic ? (
                          <Globe className="h-3 w-3" />
                        ) : (
                          <Lock className="h-3 w-3" />
                        )}
                        {session.archeIsPublic ? 'Public' : 'Private'}
                      </span>
                      {session.archeIsListed && (
                        <span className="flex items-center gap-1">
                          <Store className="h-3 w-3" />
                          Listed
                        </span>
                      )}
                      <span>{formatPricing(session)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      <span>
                        {formatDistanceToNow(new Date(session.updatedAt), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                    <span>
                      {formatCredits(session.totalCostCents)} credits (
                      {formatDollars(session.totalCostCents)})
                    </span>
                  </div>

                  {/* Delete button */}
                  <div className="mt-3 flex justify-end">
                    <AlertDialog onOpenChange={(open) => !open && setDeleteConfirmText('')}>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Session</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this session? This action cannot be
                            undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="py-4">
                          <p className="text-sm text-muted-foreground mb-2">
                            Type <span className="font-mono font-semibold">delete</span> to confirm:
                          </p>
                          <Input
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="delete"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteConfirmText.toLowerCase() !== 'delete'}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSession(session.sessionId);
                              setDeleteConfirmText('');
                            }}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
